import { useEffect } from 'react';

/** The CSS variable other chrome reads (ui/tokens.css declares it as 0px). */
export const KB_INSET_VAR = '--kb-inset';

/**
 * Fields in the editor's canvas (rich text, section title, connector label, table and task cells: all under the viewport
 * layer) and the docked format bar are the editor's own business: Editor.tsx publishes `--os-keyboard-inset` and moves the
 * camera for them. Any element can opt out with `data-kb-inset-ignore`.
 */
export const KB_INSET_IGNORE = '[data-layer="viewport"], [data-docked], [data-kb-inset-ignore]';

const TEXT_INPUT = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]):not([type="color"]):not([type="file"]):not([type="image"]):not([type="hidden"])';
const EDITABLE = `${TEXT_INPUT}, textarea, select, [contenteditable]:not([contenteditable="false"])`;

/** Pixels of the layout viewport the on-screen keyboard hides: what the visual viewport (height and scroll) no longer covers. */
export function computeKeyboardInset(layoutHeight: number, visual: { height: number; offsetTop: number }): number {
  return Math.max(0, Math.round(layoutHeight - visual.height - visual.offsetTop));
}

/** A field that raises the keyboard and is not the editor's canvas text. */
export function wantsKeyboardInset(el: EventTarget | null): boolean {
  return el instanceof Element && el.matches(EDITABLE) && !el.closest(KB_INSET_IGNORE);
}

/**
 * Publishes `--kb-inset` on `<html>` (px) while a text field has focus, from `window.visualViewport`; `0px` otherwise.
 * Sheets, the palette, the comment composer and toasts add it to their bottom offset. Listens to the visual viewport only
 * while such a field is focused, updates at most once per frame, and does nothing without `visualViewport`.
 * The caller mounts it once (Providers).
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    let active = false;
    let frame: number | null = null;

    const write = () => {
      frame = null;
      root.style.setProperty(KB_INSET_VAR, `${computeKeyboardInset(window.innerHeight, vv)}px`);
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(write);
    };
    const start = () => {
      if (active) return;
      active = true;
      vv.addEventListener('resize', schedule);
      vv.addEventListener('scroll', schedule);
      write(); // the field already has focus: do not wait a frame
    };
    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      if (active) {
        vv.removeEventListener('resize', schedule);
        vv.removeEventListener('scroll', schedule);
      }
      active = false;
      root.style.setProperty(KB_INSET_VAR, '0px');
    };
    const onFocusIn = (e: FocusEvent) => {
      if (wantsKeyboardInset(e.target)) start();
      else if (active) stop(); // moved to a canvas field or a non-text control: the keyboard is not ours
    };
    const onFocusOut = (e: FocusEvent) => {
      // Field to field keeps the keyboard up: relatedTarget is where the focus goes.
      if (active && !wantsKeyboardInset(e.relatedTarget)) stop();
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    if (wantsKeyboardInset(document.activeElement)) start();
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      stop();
    };
  }, []);
}
